/**
 * Redux Cache Persistence Layer
 * 
 * Saves catalog data to sessionStorage so users see content instantly
 * on page reload. Uses a stale-while-revalidate pattern:
 * - Hydrate from sessionStorage → instant render
 * - Background re-fetch validates data freshness
 * 
 * Only persists lightweight catalog slices, NOT auth or large transactional data.
 */

const STORAGE_KEY = 'rm_redux_cache';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes max age for persisted data

// Slices worth persisting (lightweight catalogs only)
// NOTE: 'reservation' is intentionally excluded — it's transactional data
// that changes frequently and must always be fetched fresh from the server.
const PERSISTED_SLICES = [
  'client',
  'destination',
  'hotel',
  'aeroline',
  'tour',
  'operator',
];

/**
 * Load persisted state from sessionStorage.
 * Returns a partial state object to merge with initial Redux state.
 */
export function loadPersistedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;

    const { timestamp, state } = JSON.parse(raw);

    // Don't use data older than MAX_AGE
    if (Date.now() - timestamp > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return undefined;
    }

    return state;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
}

/**
 * Save current Redux state to sessionStorage.
 * Debounced — call from store subscriber.
 */
let saveTimeout = null;
export function saveStateToStorage(state) {
  // Debounce to avoid excessive writes
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    try {
      const partial = {};
      PERSISTED_SLICES.forEach((key) => {
        if (state[key] && state[key].status === 'successful') {
          partial[key] = {
            ...state[key],
            // Mark as cached so useCachedFetch knows to re-validate
            status: 'successful',
          };
        }
      });

      // Only save if we have at least one slice worth saving
      if (Object.keys(partial).length > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          timestamp: Date.now(),
          state: partial,
        }));
      }
    } catch {
      // sessionStorage full or unavailable — silently fail
    }
  }, 2000);
}
